import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type ResultadoPlanejamento } from '../api';
import { useApp } from '../estado';
import { useRecurso } from '../ganchos';
import { formatarDataHora, formatarMoeda, ROTULO_RECEITA } from '../formatacao';
import {
  Aviso,
  Carregando,
  EstadoVazio,
  Indicador,
  Modal,
  StatusBadge,
  TituloPagina,
} from '../componentes/ui';

export function VisaoGeral() {
  const { provedor, notificar } = useApp();
  const navegar = useNavigate();
  const resumo = useRecurso(() => api.resumo(), []);
  const [executando, setExecutando] = useState<'planejar' | 'simular' | null>(null);
  const [confirmarSimulacao, setConfirmarSimulacao] = useState(false);
  const fuso = provedor?.fuso ?? 'America/Sao_Paulo';

  // A fila muda sozinha: o worker envia enquanto a tela está aberta.
  useEffect(() => {
    const id = window.setInterval(() => {
      void resumo.recarregar();
    }, 10_000);
    return () => window.clearInterval(id);
  }, [resumo.recarregar]);

  const planejar = async () => {
    setExecutando('planejar');
    try {
      const resultado = await api.planejar();
      notificar(
        resultado.avisos.length > 0 ? 'info' : 'sucesso',
        [
          resultado.fonteEventos === 'elo' ? 'Fonte dos Eventos: ELO.' : '',
          resultado.criados === 0
            ? 'Nenhum Disparo novo: a régua já estava planejada para hoje.'
            : `${resultado.criados} Disparo(s) planejado(s) para hoje.`,
          ...resultado.avisos,
        ].join(' '),
      );
      await resumo.recarregar();
    } catch (erro) {
      notificar('erro', `Não foi possível planejar: ${(erro as Error).message}`);
    } finally {
      setExecutando(null);
    }
  };

  const simular = async () => {
    setConfirmarSimulacao(false);
    setExecutando('simular');
    try {
      const resultado: ResultadoPlanejamento = await api.simular();
      notificar(
        resultado.avisos.length > 0 ? 'info' : 'sucesso',
        [
          resultado.fonteEventos === 'elo' ? 'Fonte dos Eventos: ELO.' : '',
          `${resultado.criados} Disparo(s) planejado(s); ${resultado.liberadosAgora ?? 0} antecipado(s) para envio imediato.`,
          ...resultado.avisos,
        ].join(' '),
      );
      await resumo.recarregar();
    } catch (erro) {
      notificar('erro', `Não foi possível simular: ${(erro as Error).message}`);
    } finally {
      setExecutando(null);
    }
  };

  if (resumo.carregando && !resumo.dados) return <Carregando texto="Lendo o dia do Provedor…" />;

  if (resumo.erro && !resumo.dados) {
    return (
      <Aviso tipo="erro" titulo="Não foi possível ler o resumo">
        {resumo.erro}{' '}
        <button type="button" className="link-botao" onClick={() => void resumo.recarregar()}>
          Tentar de novo
        </button>
      </Aviso>
    );
  }

  const dados = resumo.dados;
  if (!dados) return null;

  const hoje = dados.disparos.hoje;
  const enviados = hoje.enviado ?? 0;
  const pendentesHoje = hoje.pendente ?? 0;
  const elo = dados.integracoes.elo;
  const canal = dados.integracoes.canal;

  return (
    <>
      <TituloPagina
        titulo={`Bom dia, ${provedor?.nome ?? 'Provedor'}`}
        descricao={`Hoje é ${new Intl.DateTimeFormat('pt-BR', {
          timeZone: fuso,
          weekday: 'long',
          day: '2-digit',
          month: 'long',
        }).format(new Date())}. A Janela de Envio vai das ${dados.provedor?.janela.inicio ?? '08:00'} às ${
          dados.provedor?.janela.fim ?? '20:00'
        }.`}
        acoes={
          <>
            <button
              type="button"
              className="botao botao--secundario"
              onClick={() => void planejar()}
              disabled={executando !== null}
            >
              {executando === 'planejar' ? 'Planejando…' : 'Planejar o dia'}
            </button>
            <button
              type="button"
              className="botao botao--primario"
              onClick={() => setConfirmarSimulacao(true)}
              disabled={executando !== null}
            >
              {executando === 'simular' ? 'Simulando…' : 'Simular agora'}
            </button>
          </>
        }
      />

      {elo.modo === 'demo' || canal === null ? (
        <Aviso tipo="alerta" titulo="Ambiente de demonstração">
          {elo.modo === 'demo'
            ? 'O ERP está em modo demonstração: o desbloqueio é simulado e nenhuma conexão é alterada. '
            : 'Nenhum canal ativo: os Disparos ficam pendentes até você cadastrar a fila da XMAX. '}
          <button
            type="button"
            className="link-botao"
            onClick={() => navegar('/configuracoes')}
          >
            Revisar integrações
          </button>
        </Aviso>
      ) : null}

      <section className="grade grade--kpi" aria-label="Indicadores de hoje">
        <article className="kpi kpi--marca">
          <span className="kpi__rotulo">Pendentes agora</span>
          <span className="kpi__valor">{dados.disparos.pendentes}</span>
          <span className="kpi__detalhe">{pendentesHoje} planejado(s) para hoje</span>
        </article>
        <article className="kpi kpi--ok">
          <span className="kpi__rotulo">Enviados hoje</span>
          <span className="kpi__valor">{enviados}</span>
          <span className="kpi__detalhe">
            {enviados === 0 ? 'Aguardando recibo do canal' : 'Aceitos pelo canal oficial'}
          </span>
        </article>
        <article className="kpi kpi--erro">
          <span className="kpi__rotulo">Falhas em aberto</span>
          <span className="kpi__valor">{dados.disparos.falhasAbertas}</span>
          <span className="kpi__detalhe">
            {dados.disparos.falhasAbertas === 0 ? 'Nada exige atenção' : 'Revise em Disparos'}
          </span>
        </article>
        <article className="kpi kpi--info">
          <span className="kpi__rotulo">Eventos importados</span>
          <span className="kpi__valor">{dados.eventos.faturas}</span>
          <span className="kpi__detalhe">
            {dados.eventos.titulares} Titular(es) com data de nascimento
          </span>
        </article>
      </section>

      <div className="grade grade--2">
        <section className="cartao" aria-labelledby="integracao-titulo">
          <div className="cartao__cabecalho">
            <div className="cartao__titulo">
              <h2 id="integracao-titulo">Integrações</h2>
              <p className="cartao__descricao">
                O motor lê o ERP pela conexão oficial do Provedor e envia pela fila de WhatsApp
                contratada. Nenhum acesso direto ao banco do ERP.
              </p>
            </div>
            <button
              type="button"
              className="botao botao--fantasma botao--pequeno"
              onClick={() => navegar('/configuracoes')}
            >
              Configurar
            </button>
          </div>

          <ul className="pilha pilha--apertada" style={{ listStyle: 'none', padding: 0 }}>
            <li className="linha linha--separada">
              <span>
                <strong>Conexão com o ERP</strong>
                <br />
                <span className="texto-pequeno">
                  {elo.modo === 'real'
                    ? (elo.baseUrl ?? 'Conectado')
                    : elo.modo === 'demo'
                      ? 'Modo demonstração — nenhuma escrita no ERP'
                      : 'Não configurado'}
                </span>
              </span>
              <Indicador
                estado={
                  elo.modo === 'real' ? (elo.ultimoTesteOk === false ? 'erro' : 'ok') : 'alerta'
                }
              >
                {elo.modo === 'real'
                  ? elo.ultimoTesteOk === false
                    ? 'Com falha no teste'
                    : 'Conectado'
                  : elo.modo === 'demo'
                    ? 'Demonstração'
                    : 'Sem conexão'}
              </Indicador>
            </li>
            <li className="linha linha--separada">
              <span>
                <strong>Canal WhatsApp</strong>
                <br />
                <span className="texto-pequeno">
                  {canal
                    ? `Fila #${canal.fila} · ${canal.vazaoPorMinuto} msg/min`
                    : 'Nenhum canal ativo'}
                </span>
              </span>
              <Indicador estado={canal?.ativo ? 'ok' : 'erro'}>
                {canal?.ativo ? 'Ativo' : 'Inativo'}
              </Indicador>
            </li>
            <li className="linha linha--separada">
              <span>
                <strong>Réguas ligadas</strong>
                <br />
                <span className="texto-pequeno">
                  {dados.receitasAtivas} de {dados.receitasTotal} receitas
                </span>
              </span>
              <button
                type="button"
                className="botao botao--secundario botao--pequeno"
                onClick={() => navegar('/receitas')}
              >
                Abrir réguas
              </button>
            </li>
            <li className="linha linha--separada">
              <span>
                <strong>Fonte de Eventos</strong>
                <br />
                <span className="texto-pequeno">
                  {dados.ultimaImportacao
                    ? `Último arquivo: ${dados.ultimaImportacao.arquivo} em ${formatarDataHora(
                        dados.ultimaImportacao.criadoEm,
                        fuso,
                      )}`
                    : 'Nenhuma importação ainda'}
                </span>
              </span>
              <button
                type="button"
                className="botao botao--secundario botao--pequeno"
                onClick={() => navegar('/eventos')}
              >
                Importar
              </button>
            </li>
          </ul>
        </section>

        <section className="cartao" aria-labelledby="status-titulo">
          <div className="cartao__cabecalho">
            <div className="cartao__titulo">
              <h2 id="status-titulo">Disparos de hoje por status</h2>
              <p className="cartao__descricao">
                Status é sempre cor, ícone e texto — nunca só cor.
              </p>
            </div>
          </div>
          {Object.keys(hoje).length === 0 ? (
            <EstadoVazio
              titulo="Nenhum Disparo hoje"
              texto="Planeje o dia ou simule agora para ver a régua em ação."
              icone="◷"
            />
          ) : (
            <ul className="pilha pilha--apertada" style={{ listStyle: 'none', padding: 0 }}>
              {Object.entries(hoje)
                .sort(([, a], [, b]) => b - a)
                .map(([status, total]) => (
                  <li key={status} className="linha linha--separada">
                    <StatusBadge status={status} />
                    <strong className="numero">{total}</strong>
                  </li>
                ))}
            </ul>
          )}
        </section>
      </div>

      <section className="cartao" aria-labelledby="proximos-titulo">
        <div className="cartao__cabecalho">
          <div className="cartao__titulo">
            <h2 id="proximos-titulo">Próximos da fila</h2>
            <p className="cartao__descricao">
              Os cinco Disparos mais próximos, já respeitando a Janela de Envio.
            </p>
          </div>
          <button
            type="button"
            className="botao botao--fantasma botao--pequeno"
            onClick={() => navegar('/disparos')}
          >
            Ver todos
          </button>
        </div>

        {dados.disparos.proximos.length === 0 ? (
          <EstadoVazio
            titulo="A fila está vazia"
            texto="Nada agendado. Você pode planejar o dia ou ativar mais receitas."
            icone="✓"
          />
        ) : (
          <div className="tabela-rolagem">
            <table>
              <caption className="sr-apenas">Próximos Disparos</caption>
              <thead>
                <tr>
                  <th scope="col">Titular</th>
                  <th scope="col">Receita</th>
                  <th scope="col">Quando</th>
                  <th scope="col">Valor</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {dados.disparos.proximos.map((disparo) => (
                  <tr key={disparo.id}>
                    <td>
                      <strong>{disparo.nome}</strong>
                      <br />
                      <span className="texto-pequeno">{disparo.documento}</span>
                    </td>
                    <td>{ROTULO_RECEITA[disparo.receita] ?? disparo.receita}</td>
                    <td className="numero">
                      {formatarDataHora(disparo.agendadoPara, fuso)}
                    </td>
                    <td className="numero">{formatarMoeda(disparo.valorCentavos)}</td>
                    <td>
                      <StatusBadge status={disparo.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="cartao cartao--compacto">
        <div className="cartao__cabecalho">
          <div className="cartao__titulo">
            <h2>Como o motor decide</h2>
          </div>
        </div>
        <div className="grade grade--3">
          <div>
            <strong>1 · Evento</strong>
            <p className="texto-pequeno">
              A fatura vence, atrasa ou o Titular faz aniversário. No modo real, o planejamento consulta o ELO.
            </p>
          </div>
          <div>
            <strong>2 · Receita</strong>
            <p className="texto-pequeno">
              A régua ligada define os dias, o horário e o template. Fora da Janela de Envio, o
              Disparo é reprogramado.
            </p>
          </div>
          <div>
            <strong>3 · Canal</strong>
            <p className="texto-pequeno">
              O worker envia pela fila daquele Provedor com vazão própria e registra recibo e
              auditoria.
            </p>
          </div>
        </div>
      </section>

      <Modal
        aberto={confirmarSimulacao}
        titulo="Simular agora"
        descricao="O motor planeja o dia e antecipa os Disparos para este instante. Se o canal estiver em modo real, mensagens reais saem para os Titulares."
        onFechar={() => setConfirmarSimulacao(false)}
        rodape={
          <>
            <button
              type="button"
              className="botao botao--secundario"
              onClick={() => setConfirmarSimulacao(false)}
            >
              Cancelar
            </button>
            <button type="button" className="botao botao--primario" onClick={() => void simular()}>
              Simular e enviar agora
            </button>
          </>
        }
      >
        <Aviso tipo="info" titulo="O que acontece">
          <ul style={{ margin: 0 }}>
            <li>Somente as receitas ativas são consideradas.</li>
            <li>Opt-out, ausência de telefone e falta de template continuam bloqueando o envio.</li>
            <li>Cada Disparo é único por chave: simular duas vezes não duplica mensagem.</li>
          </ul>
        </Aviso>
      </Modal>
    </>
  );
}
